import {Card, CardBody} from '@heroui/react';
import {Icon} from '@iconify/react';
import {createFileRoute, SearchSchemaInput, useNavigate} from '@tanstack/react-router';
import {useEffect} from 'react';
import PButton from '../../components/PButton.tsx';
import Header from '../../Header.tsx';
import {useJwtUser} from '../../JWTAuthProvider.tsx';
import {setAdsUserData} from '../../utilities/adsEmailMatching.ts';
import {parseRedirectUrl} from '../../utilities/utilities.ts';

// Distinctive post-signup URL used as the Google Ads / analytics conversion page. Successful
// signups land here (see SignupView), the conversion fires, then we forward to the app.
export const Route = createFileRoute('/signup/thank-you')({
    validateSearch: (search: Record<string, unknown> & SearchSchemaInput) => ({
        next: search.next as string | undefined,
        type: search.type as string | undefined,
    }),
    component: RouteComponent,
});

const AUTO_REDIRECT_MS = 3000;

// Google Ads conversion "Send to" labels per signup type. Fill each from Google Ads →
// Conversions → the action → its "Send to" value, e.g. 'AW-17042305006/AbC-D_efG'.
// Leave empty to skip firing that conversion (the sign_up event still fires either way).
const CONVERSION_LABELS: Record<string, string> = {
    player:    'AW-17042305006/1YDuCLuhgcscEO7fs74_',
    organizer: 'AW-17042305006/ec_JCKW6_8ocEO7fs74_',
};

// Fire once per account, ever, within this browser session. A useRef guard resets on every
// mount, and this page auto-redirects to the dashboard — so Back used to remount it and count
// another conversion each press. Keyed by uid so a genuinely different signup still fires.
const firedKey = (uid: string) => `sp_signup_fired:${uid}`;

// Only an account created within this window counts as a signup landing here — an old account
// revisiting a bookmark or shared link is not a conversion.
const FRESH_ACCOUNT_MS = 10 * 60 * 1000;

function RouteComponent() {
    const navigate = useNavigate();
    const {next, type} = Route.useSearch();
    const user = useJwtUser();

    const goNext = () => {
        navigate((parseRedirectUrl(next) || {to: '/profiles/me/'}) as any);
    };

    useEffect(() => {
        // The SPA route change doesn't reload the page, so push an explicit sign_up + conversion to
        // the gtag tag so it registers for this URL. Gated on: an authenticated user (anonymous
        // visitors who load the URL are not signups), an account minutes old, and a per-uid
        // sessionStorage flag that survives remounts. Respect the #no-tracking opt-out.
        const gtag = (window as any).gtag;
        const isFresh = !!user?.created_at && Date.now() - new Date(user.created_at).getTime() < FRESH_ACCOUNT_MS;
        if (gtag && user?.uid && isFresh && !sessionStorage.getItem(firedKey(user.uid))
            && window.location.hash !== '#no-tracking') {
            sessionStorage.setItem(firedKey(user.uid), '1');
            setAdsUserData(user.email); // enhanced conversion matching — before the conversion event
            // GA-style signup event (all signups), plus the Google Ads conversion for this role.
            gtag('event', 'sign_up', {method: 'scorepal', signup_type: type || 'unknown'});
            const label = CONVERSION_LABELS[type || ''];
            if (label) {
                // transaction_id is Google's dedupe key — same uid everywhere this conversion fires.
                gtag('event', 'conversion', {send_to: label, transaction_id: user.uid});
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.uid]);

    useEffect(() => {
        const timer = setTimeout(goNext, AUTO_REDIRECT_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <title>Welcome to ScorePal</title>
            <meta name="robots" content="noindex, nofollow" />

            <Header backUrl={"/"} isPublicPage={true} />

            <div className="max-w-md mx-auto flex w-full flex-col px-4 mt-8">
                <Card>
                    <CardBody className="text-center p-8">
                        <div className="flex justify-center mb-4">
                            <div className="w-16 h-16 rounded-full bg-success/15 flex items-center justify-center">
                                <Icon icon="material-symbols-light:check-circle" className="text-5xl text-success" />
                            </div>
                        </div>
                        <h1 className="text-2xl font-bold mb-2">Welcome to ScorePal 🎉</h1>
                        <p className="text-default-500 mb-6">
                            Your account is ready. We're taking you to your dashboard…
                        </p>
                        <PButton color="primary" onPress={goNext}
                                 endContent={<Icon icon="material-symbols-light:arrow-forward" className="text-lg" />}>
                            Continue to your dashboard
                        </PButton>
                    </CardBody>
                </Card>
            </div>
        </>
    );
}
